import React, { useEffect, useMemo, useRef } from 'react';
import { AnnotationLane, ANNOTATION_LANE_HEIGHT } from './AnnotationLane';
import { Connectors } from './Connectors';
import { GridLines } from './GridLines';
import { HoverTooltip } from './HoverTooltip';
import { ItemLabelChip } from './ItemLabelChip';
import { LaneLabels, LANE_BAND_VPAD } from './LaneLabels';
import { computeTimelineLayout } from './layout';
import { MoreBadge } from './MoreBadge';
import { RulerBands } from './RulerBands';
import { computeColWidth, createDayScale } from './scale';
import { ETAnnotation, ETBundle, ETItem, ETLane, ETRuler, ETTrigger } from './types';
import { useExperienceTimelineViewState, ExperienceTimelineViewState } from './useExperienceTimelineViewState';
import { expandClusterView, setHoveredIdView, toggleLaneView } from './viewReducers';
import { WeekHeader, WeekLabelRenderer } from './WeekHeader';
import { AutomationGlyph, MessageGlyph, TaskEndBar, TaskStartGlyph, TimelineSvgDefs } from './glyphs';
import { getZoomConfig } from './zoom';
import { useResizeObserver } from '../../hooks/useResizeObserver';
import { getReadableForeground } from '../../services/color';
import { timelineColors } from './tokens';

export {
  useExperienceTimelineViewState,
  DEFAULT_TIMELINE_VIEW_STATE
} from './useExperienceTimelineViewState';
export type { ExperienceTimelineViewState } from './useExperienceTimelineViewState';
export * from './types';
export { ZOOM_LEVELS, clampZoom } from './zoom';
export { Controls } from './Controls';
export { Legend } from './Legend';
export { KIND_LABEL } from './labels';
export {
  zoomInView,
  zoomOutView,
  toggleHideLabelsView,
  toggleLaneView,
  setLanesCollapsedView,
  expandClusterView,
  setHoveredIdView
} from './viewReducers';

const LABEL_WIDTH = 150;
const TOP_PADDING = 30;
const HEADER_HEIGHT = 34;
const CHROME_HEIGHT = TOP_PADDING + HEADER_HEIGHT;
const DAY_GRIDLINE_OVERVIEW_THRESHOLD = 60;

export type ExperienceTimelineProps = {
  lanes: ETLane[];
  items: ETItem[];
  bundles?: ETBundle[];
  triggers?: ETTrigger[];
  rulers?: ETRuler[];
  annotations?: ETAnnotation[];
  renderAnnotation?: (a: ETAnnotation) => React.ReactNode;

  view: ExperienceTimelineViewState;
  onViewChange: (updater: (prev: ExperienceTimelineViewState) => ExperienceTimelineViewState) => void;

  onSelectItem?: (id: string, meta: unknown) => void;
  renderTooltip?: (item: ETItem) => React.ReactNode;

  onItemMove?: (
    id: string,
    next: { startDay: number; endDay: number },
    prev: { startDay: number; endDay: number },
    deltaDays: number,
    meta: unknown
  ) => void;

  minDays?: number;
  dragActive?: boolean;
  weekLabel?: WeekLabelRenderer;

  // Reports which lane ids currently have a collapse-eligible cluster (a
  // day-overlap cluster with >2 rows). Lane eligibility depends on the
  // packed layout (day-overlap + zoom-dependent row/label sizing), which is
  // computed inside this component — callers that want a "Collapse all"
  // control (which needs to know whether to show itself, and whether every
  // eligible lane is currently collapsed) have no other way to see it.
  onEligibleLanesChange?: (laneIds: string[]) => void;
};

export const ExperienceTimeline = ({
  lanes,
  items,
  bundles = [],
  triggers = [],
  rulers = [],
  annotations = [],
  renderAnnotation,
  view,
  onViewChange,
  onSelectItem,
  renderTooltip,
  minDays = 0,
  dragActive = false,
  weekLabel,
  onEligibleLanesChange
}: ExperienceTimelineProps) => {
  const [containerWidth, setContainerWidth] = React.useState(0);
  const containerRef = useResizeObserver<HTMLDivElement>((entry) =>
    setContainerWidth(entry.contentRect.width)
  );

  const zoomConfig = getZoomConfig(view.zoom);

  const dayMin = Math.min(0, ...items.map((i) => i.startDay), ...rulers.map((r) => r.day));
  const rawDayMax = Math.max(0, ...items.map((i) => i.endDay), ...rulers.map((r) => r.day));
  // `minDays` reserves a minimum visible range (like the old engine's
  // `minUnits`) — it pads the end of the domain, it is not itself a day index.
  const dayMax = Math.max(rawDayMax, dayMin + minDays - 1);
  const numDays = dayMax - dayMin + 1;

  const colWidth = computeColWidth({
    containerWidth,
    labelWidth: LABEL_WIDTH,
    numDays,
    minColWidth: zoomConfig.minColWidth
  });
  const scale = useMemo(
    () => createDayScale({ dayMin, dayMax, colWidth, labelWidth: LABEL_WIDTH }),
    [dayMin, dayMax, colWidth]
  );

  const layout = useMemo(
    () =>
      computeTimelineLayout({
        lanes,
        items,
        bundles,
        colWidth,
        rowHeight: zoomConfig.rowHeight,
        rowGap: zoomConfig.rowGap,
        collapsedLanes: view.collapsedLanes,
        expandedClusters: view.expandedClusters
      }),
    [lanes, items, bundles, colWidth, zoomConfig.rowHeight, zoomConfig.rowGap, view.collapsedLanes, view.expandedClusters]
  );

  const itemById = useMemo(() => new Map(items.map((it) => [it.id, it])), [items]);
  const laneById = useMemo(() => new Map(lanes.map((l) => [l.id, l])), [lanes]);

  const eligibleLaneIds = useMemo(
    () => layout.lanes.filter((l) => l.collapsible).map((l) => l.laneId),
    [layout.lanes]
  );
  const lastReportedEligibleLaneIds = useRef<string[] | null>(null);
  useEffect(() => {
    if (!onEligibleLanesChange) return;
    const prev = lastReportedEligibleLaneIds.current;
    const same =
      prev &&
      prev.length === eligibleLaneIds.length &&
      prev.every((id, i) => id === eligibleLaneIds[i]);
    if (same) return;
    lastReportedEligibleLaneIds.current = eligibleLaneIds;
    onEligibleLanesChange(eligibleLaneIds);
  }, [eligibleLaneIds, onEligibleLanesChange]);

  const hasAnnotations = annotations.length > 0;
  const annotationTop = CHROME_HEIGHT;
  const contentTop = CHROME_HEIGHT + (hasAnnotations ? ANNOTATION_LANE_HEIGHT : 0);
  // Reserve the lane tint band's bottom overhang so it doesn't spill past the
  // canvas and trip a vertical scrollbar when the rows themselves fit.
  const totalHeight = contentTop + layout.totalHeight + LANE_BAND_VPAD;

  const dayLinesEnabled =
    zoomConfig.showDayGridlines || (view.zoom === 0 && numDays <= DAY_GRIDLINE_OVERVIEW_THRESHOLD);

  // Labels are available at Week/Detail zoom and can be turned off via the
  // hide-labels toggle. Overview never shows them regardless.
  const showLabels = zoomConfig.showLabels && !view.hideLabels;

  const distinctColors = useMemo(() => Array.from(new Set(items.map((i) => i.color))), [items]);

  const hoveredItem = view.hoveredId ? itemById.get(view.hoveredId) ?? null : null;

  return (
    <div ref={containerRef} style={{ position: 'relative', overflow: 'auto' }}>
      <div style={{ position: 'relative', width: scale.totalWidth, height: totalHeight }}>
        <div style={{ position: 'absolute', left: 0, top: TOP_PADDING, width: scale.totalWidth, height: HEADER_HEIGHT }}>
          <WeekHeader scale={scale} height={HEADER_HEIGHT} renderLabel={weekLabel} />
        </div>

        <RulerBands rulers={rulers} scale={scale} canvasHeight={totalHeight} headerHeight={CHROME_HEIGHT} />

        <div
          style={{
            position: 'absolute',
            left: 0,
            top: CHROME_HEIGHT,
            width: scale.totalWidth,
            height: totalHeight - CHROME_HEIGHT
          }}
        >
          <GridLines
            scale={scale}
            height={totalHeight - CHROME_HEIGHT}
            dayLinesEnabled={dayLinesEnabled}
            dayColor={timelineColors.lineSubtle}
            weekColor={timelineColors.lineStrong}
          />
        </div>

        {hasAnnotations && (
          <AnnotationLane
            annotations={annotations}
            scale={scale}
            top={annotationTop}
            canvasHeight={totalHeight}
            renderAnnotation={renderAnnotation}
            borderColor={timelineColors.border}
          />
        )}

        <div style={{ position: 'absolute', left: 0, top: contentTop, width: scale.totalWidth, height: layout.totalHeight }}>
          <LaneLabels
            lanes={lanes}
            laneById={layout.laneById}
            labelWidth={LABEL_WIDTH}
            canvasWidth={scale.totalWidth}
            onToggleLane={(laneId) => onViewChange((v) => toggleLaneView(v, laneId))}
          />

          <svg
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: scale.totalWidth,
              height: layout.totalHeight,
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            <TimelineSvgDefs colors={distinctColors} />
            <Connectors items={items} layout={layout} scale={scale} triggers={triggers} suppressed={dragActive} />
          </svg>

          {items.map((item) => {
            const il = layout.items[item.id];
            if (!il || il.hidden) return null;
            const cx = scale.xOfDayCenter(item.startDay);
            const cy = il.y;
            // Deactivated items sit on a flat grey-300 fill; their glyph icon
            // drops to a muted grey rather than the usual readable-on-fill (near
            // black) foreground.
            const foreground = item.deactivated
              ? timelineColors.textSubtle
              : getReadableForeground(item.color);
            const isVariableStart = !!item.variableStart;

            const handlers = {
              onMouseEnter: () => onViewChange((v) => setHoveredIdView(v, item.id)),
              onMouseLeave: () => onViewChange((v) => setHoveredIdView(v, null)),
              onClick: () => onSelectItem?.(item.id, item.meta)
            };
            if (item.kind === 'MESSAGE') {
              return (
                <React.Fragment key={item.id}>
                  <div
                    {...handlers}
                    style={{
                      position: 'absolute',
                      left: cx - 11,
                      top: cy - 11,
                      cursor: 'pointer',
                      zIndex: 2,
                      borderRadius: '50%'
                    }}
                  >
                    <MessageGlyph color={item.color} foreground={foreground} variableStart={isVariableStart} />
                  </div>
                  {showLabels && (
                    <ItemLabelChip x={cx + 14} y={cy} maxWidth={colWidth * 2} label={item.label} />
                  )}
                </React.Fragment>
              );
            }

            if (item.kind === 'AUTOMATION') {
              return (
                <React.Fragment key={item.id}>
                  <div
                    {...handlers}
                    style={{
                      position: 'absolute',
                      left: cx - 11,
                      top: cy - 11,
                      cursor: 'pointer',
                      zIndex: 2,
                      borderRadius: '50%'
                    }}
                  >
                    <AutomationGlyph color={item.color} foreground={foreground} />
                  </div>
                  {showLabels && (
                    <ItemLabelChip x={cx + 14} y={cy} maxWidth={colWidth * 2} label={item.label} />
                  )}
                </React.Fragment>
              );
            }

            // TASK
            const ex = scale.xOfDayCenter(item.endDay);
            const hasDuration = ex !== cx;
            return (
              <React.Fragment key={item.id}>
                {hasDuration && (
                  <div
                    style={{
                      position: 'absolute',
                      left: cx + 11,
                      top: cy - 1,
                      width: Math.max(0, ex - cx - 12.5),
                      height: 2,
                      background: item.color,
                      zIndex: 1
                    }}
                  />
                )}
                <div
                  {...handlers}
                  style={{
                    position: 'absolute',
                    left: cx - 11,
                    top: cy - 11,
                    cursor: item.movable ? 'grab' : 'pointer',
                    zIndex: 2,
                    borderRadius: '50%'
                  }}
                >
                  <TaskStartGlyph color={item.color} foreground={foreground} variableStart={isVariableStart} />
                </div>
                {hasDuration && (
                  <div
                    {...handlers}
                    style={{ position: 'absolute', left: ex - 1.5, top: cy - 21 / 2, zIndex: 2 }}
                  >
                    <TaskEndBar color={item.color} />
                  </div>
                )}
                {showLabels && (
                  <ItemLabelChip
                    x={cx + 14}
                    y={cy}
                    maxWidth={Math.max(colWidth * 2, ex - cx)}
                    label={item.label}
                  />
                )}
              </React.Fragment>
            );
          })}

          {layout.badges.map((badge) => {
            const anchor = itemById.get(badge.anchorItemId);
            const il = layout.items[badge.anchorItemId];
            if (!anchor || !il) return null;
            const x = scale.xOfDayCenter(anchor.startDay);
            return (
              <MoreBadge
                key={badge.key}
                x={x}
                y={il.y}
                count={badge.count}
                belowIcon={showLabels}
                onClick={() => onViewChange((v) => expandClusterView(v, badge.key))}
              />
            );
          })}
        </div>

        {hoveredItem &&
          (() => {
            const il = layout.items[hoveredItem.id];
            if (!il || il.hidden) return null;
            const x = scale.xOfDayCenter(hoveredItem.startDay);
            const y = contentTop + il.y;
            if (renderTooltip) return renderTooltip(hoveredItem);
            const actorLabel = laneById.get(hoveredItem.laneId)?.label ?? '';
            return <HoverTooltip x={x} y={y} item={hoveredItem} actorLabel={actorLabel} />;
          })()}
      </div>
    </div>
  );
};
